/**
 * An admin's read-only "view as a gardener" (card E16): which gardener is
 * being viewed, kept for this tab only, and the calls that start and end it.
 *
 * The choice is UI state, not gardener data: it is kept in sessionStorage
 * (gardenMembers' view-as helpers) so it ends with the tab and with sign-out. While it
 * is set, apiClient adds `X-Grow-View-As: <gardener's id>` (VIEW_AS_HEADER) to
 * every request, and the backend answers as that gardener, read-only: it
 * refuses every change with a sentence of its own, which the app shows.
 *
 * Starting and ending a view empties the query cache first, so nothing from
 * one garden is ever drawn in another, then loads the page afresh.
 *
 * Who uses it: apiClient (the header and the refusal toast), AuthContext
 * (forgetting on sign-out), the ViewAsBanner in Layout, and the ViewAsPage.
 *
 * @module viewAs
 */

import { queryClient } from '@/config/queryClient';
import { readViewAsStored, writeViewAsStored } from './gardenMembers';

/** The request header naming the gardener being viewed: the backend's contract, not a tunable. */
export const VIEW_AS_HEADER = 'X-Grow-View-As';

/** The gardener being viewed: their user id and the name the banner shows. */
export interface ViewingAs {
  /** The gardener's user id, sent in the header. */
  id: number;
  /** The name shown in the banner. */
  name: string;
}

/**
 * Who is being viewed in this tab.
 *
 * @returns The gardener, or null when no view is on (or storage cannot be read).
 */
export function viewingAs(): ViewingAs | null {
  try {
    const raw = readViewAsStored();
    if (raw === null) return null;
    const value = JSON.parse(raw) as Partial<ViewingAs>;
    return Number.isInteger(value.id) ? { id: value.id as number, name: String(value.name ?? '') } : null;
  } catch {
    return null;
  }
}

/**
 * Starts viewing as a gardener: remembers them, empties the caches and opens Today.
 *
 * @param who - The gardener to view as.
 */
export function startViewAs(who: ViewingAs): void {
  writeViewAsStored(JSON.stringify(who));
  queryClient.clear();
  window.location.assign('/today');
}

/** Ends the view: forgets the gardener, empties the caches and reloads as the admin. */
export function exitViewAs(): void {
  forgetViewAs();
  queryClient.clear();
  window.location.assign('/admin/view-as');
}

/** Forgets the view without reloading; for signing out. */
export function forgetViewAs(): void {
  writeViewAsStored(null);
}

/**
 * Whether a request should carry the view-as header: not the admin routes
 * (/admin/*, the server's own) nor the admin pages (the Google-sign-in admin
 * pages: the plant data manager, the gallery approver), which are the admin's own.
 *
 * @param url - The request's path under the API base.
 * @returns True when the header goes with it.
 */
export function sendsViewAs(url: string | undefined): boolean {
  if (url && /^\/?admin(\/|$)/.test(url)) return false;
  return !window.location.pathname.startsWith('/admin');
}

/**
 * The server refused the view itself (not an admin, no such gardener, a bad
 * id): forgets it, empties the caches and returns to the admin's own app.
 */
export function endRefusedViewAs(): void {
  forgetViewAs();
  queryClient.clear();
  window.location.assign('/admin/view-as');
}

/**
 * Whether a request is a write the page fires by itself, not something the
 * gardener pressed: while viewing, these are dropped quietly (no refusal
 * toast) so the refusal only ever follows a press. Today that is the "seen"
 * mark on a piece of advice, sent when it is drawn (POST /todo/marks).
 *
 * @param method - The request's method.
 * @param url - The request's path under the API base.
 * @param body - The request's body, as axios holds it (an object or its JSON text).
 * @returns True when it is an automatic write.
 */
export function isAutomaticWrite(method: string | undefined, url: string | undefined, body: unknown): boolean {
  if (String(method).toLowerCase() !== 'post' || url !== '/todo/marks') return false;
  try {
    const data = typeof body === 'string' ? JSON.parse(body) : body;
    return (data as { action?: unknown } | null)?.action === 'seen';
  } catch {
    return false;
  }
}
